import { mdiAutoFix, mdiAutorenew, mdiImageOutline, mdiTextBoxOutline } from '@mdi/js'
import { Icon } from '@mdi/react'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import { ChatImageBubble, ChatTextBubble } from 'components/Chat'
import { ComposerMediaContext } from 'components/ComposerMediaContext/context'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import PPSwitch from 'components/PPSwitch'
import UsageIndicator from 'components/UsageIndicator'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addComposerImages } from 'services/compose'
import { aiWriterSelector } from 'services/compose/selectors'
import { generateCaption, generateImage } from 'services/compose/suggest/actions'
import { AIConversation, AIConversationKind } from 'services/compose/suggest/net'
import { checkProductLimit } from 'services/product'
import { message } from 'services/snackbar'
import { closeComposerContentDialog } from 'services/ui'
import { AI_IMAGES_LIMIT } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import styles from './GenerateTab.pcss'

const ASPECT_RATIO_OPTIONS: PPSelectOptions = {
  '1:1': { label: '1:1' },
  '4:5': { label: '4:5' },
  '9:16': { label: '9:16' },
  '16:9': { label: '16:9' },
  '3:2': { label: '3:2' },
  '2:3': { label: '2:3' }
}

const DEFAULT_RATIO = '1:1'
const ERROR_TEXT = 'Sorry, the image could not be generated. Try again?'
const SPINNER_SIZE = 28
const EMPTY_ICON_SIZE = '36px'
const ICON_SIZE = '20px'

type ChatMessageKind = 'prompt' | 'image' | 'error' | 'text'

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  kind: ChatMessageKind
  text?: string
  imageUrl?: string
  ratio?: string
  promptId?: string
}

const preloadImage = (url: string): Promise<void> => new Promise((resolve, reject) => {
  const img = new Image()
  img.onload = () => resolve()
  img.onerror = () => reject(new Error('preload failed'))
  img.src = url
})

export function GenerateTab(
  { conversation, prompt: initialPrompt, kind }: { conversation?: AIConversation, prompt?: string, kind?: AIConversationKind }
) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { dispatch: composerMediaDispatch } = useContext(ComposerMediaContext)

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [prompt, setPrompt] = useState(initialPrompt || '')
  const [ratio, setRatio] = useState(DEFAULT_RATIO)
  const [loading, setLoading] = useState(false)
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [mode, setMode] = useState<AIConversationKind>(conversation?.kind ?? kind ?? 'image')
  const { imageRequests, imageRequestsLimit } = useSelector(aiWriterSelector)

  const isCaption = mode === 'text'
  // Mode is switchable only on a blank canvas — once a chat starts (or a conversation loads) the session kind is locked.
  const canSwitchMode = !conversation && messages.length === 0
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setMode(conversation?.kind ?? kind ?? 'image')
  }, [conversation, kind])

  useEffect(() => {
    if (conversation && conversation.messages.length > 0) {
      setSessionId(conversation.id)
      const mappedMessages = conversation.messages.map((msg) => {
        if (msg.kind === 'image') {
          return {
            id: msg.id,
            role: 'assistant' as const,
            kind: 'image' as const,
            imageUrl: msg.imageUrl,
            ratio: msg.ratio
          }
        }
        // For text messages, map user messages as prompts
        return {
          id: msg.id,
          role: msg.role as 'user' | 'assistant',
          kind: msg.role === 'user' ? ('prompt' as const) : ('text' as const),
          text: msg.text
        }
      })
      setMessages(mappedMessages)
    }
  }, [conversation])

  useEffect(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, loading])

  const appendError = (userMsg: ChatMessage, errMsg?: string) => {
    setMessages(prev => [...prev, {
      id: `err-${Date.now()}`,
      role: 'assistant',
      kind: 'error',
      text: errMsg || ERROR_TEXT,
      promptId: userMsg.id
    }])
  }

  const runCaptionGeneration = (userMsg: ChatMessage) => {
    dispatch(generateCaption({ prompt: userMsg.text!, conversationId: sessionId }))
      .unwrap()
      .then((result: { text?: string, sessionId?: string }) => {
        if (result?.sessionId) {
          setSessionId(result.sessionId)
        }
        if (!result?.text) { return }
        setMessages(prev => [...prev, {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          kind: 'text',
          text: result.text,
          promptId: userMsg.id
        }])
      })
      .catch((e) => {
        const errMsg = e.response?.error?.message
        appendError(userMsg, errMsg)
      })
      .finally(() => setLoading(false))
  }

  const runImageGeneration = (userMsg: ChatMessage) => {
    const isWithinLimit = dispatch(checkProductLimit(AI_IMAGES_LIMIT, imageRequests + 1))
    if (!isWithinLimit) {
      setLoading(false)
      return
    }
    dispatch(generateImage({ prompt: userMsg.text!, ratio: userMsg.ratio!, sessionId }))
      .unwrap()
      .then(async (result: { image?: string, sessionId?: string }) => {
        if (result?.sessionId) {
          setSessionId(result.sessionId)
        }
        if (!result?.image) { return }
        try {
          await preloadImage(result.image)
        } catch {
          appendError(userMsg)
          return
        }
        setMessages(prev => [...prev, {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          kind: 'image',
          imageUrl: result.image,
          ratio: userMsg.ratio,
          promptId: userMsg.id
        }])
      })
      .catch((e) => {
        const errMsg = e.response?.error?.message
        appendError(userMsg, errMsg)
      })
      .finally(() => setLoading(false))
  }

  const runGeneration = (userMsg: ChatMessage) => {
    setLoading(true)
    if (isCaption) {
      runCaptionGeneration(userMsg)
    } else {
      runImageGeneration(userMsg)
    }
  }

  const onPromptChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPrompt(e.target.value)
  }

  const onSend = () => {
    const text = prompt.trim()
    if (!text || loading) { return }

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      kind: 'prompt',
      text,
      ratio
    }
    setMessages(prev => [...prev, userMsg])
    setPrompt('')
    runGeneration(userMsg)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && prompt.trim() && !loading) {
      e.preventDefault()
      onSend()
    }
  }

  const onRetry = (promptId: string) => {
    if (loading) { return }
    const userMsg = messages.find(m => m.id === promptId && m.role === 'user')
    if (!userMsg) { return }
    setMessages(prev => prev.filter(m => !(m.role === 'assistant' && m.kind === 'error' && m.promptId === promptId)))
    runGeneration(userMsg)
  }

  const onCreatePost = (imageUrl: string) => {
    dispatch(addComposerImages({ items: [{ url: imageUrl }], contextDispatch: composerMediaDispatch }))
    dispatch(message('Image added to composer.'))
    dispatch(closeComposerContentDialog())
  }

  const onNewConversation = () => {
    setMessages([])
    setSessionId(undefined)
    setPrompt('')
  }

  const renderMessage = (msg: ChatMessage) => {
    if (msg.role === 'user') {
      return (
        <ChatTextBubble
          key={msg.id}
          role="user"
          text={msg.text || ''}
          testId={`message-${msg.id}`}
        />
      )
    }

    if (msg.kind === 'image') {
      return (
        <ChatImageBubble
          key={msg.id}
          imageUrl={msg.imageUrl!}
          onCreatePost={onCreatePost}
          testId={`message-${msg.id}`}
        />
      )
    }

    if (msg.kind === 'text' && msg.role === 'assistant') {
      return (
        <ChatTextBubble
          key={msg.id}
          role="assistant"
          text={msg.text || ''}
          testId={`message-${msg.id}`}
        />
      )
    }

    // kind === 'error'
    return (
      <div
        key={msg.id}
        data-testid={`message-${msg.id}`}
        data-role="assistant"
        data-kind="error"
        className={`${styles.message} ${styles.assistant}`}
      >
        <div className={`${styles.bubble} ${styles['error-bubble']}`}>
          <span>{msg.text}</span>
          {msg.promptId && (
            <Button
              className={styles['btn-retry']}
              size="small"
              variant="text"
              color="error"
              aria-label="Try again"
              onClick={() => onRetry(msg.promptId!)}
            >
              Try again
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.messages}>
        {messages.length === 0 ? (
          <div data-testid="empty-state" className={styles.empty}>
            {canSwitchMode && (
              <PPSwitch
                id="generate-mode-switch"
                selectedValue={mode}
                onSelectedValueChange={(value) => setMode(value as AIConversationKind)}
                options={[
                  { value: 'image', label: 'Image', icon: <Icon path={mdiImageOutline} size={ICON_SIZE} /> },
                  { value: 'text', label: 'Caption', icon: <Icon path={mdiTextBoxOutline} size={ICON_SIZE} /> }
                ]}
              />
            )}
            <div className={styles['empty-hint']}>
              <Icon path={mdiAutoFix} size={EMPTY_ICON_SIZE} className={styles['empty-icon']} />
              <span>
                {isCaption ? 'Describe the caption you need and AI will write it' : 'Describe the image you need and AI will create it'}
              </span>
            </div>
          </div>
        ) : (
          messages.map(renderMessage)
        )}
        {loading && (
          isCaption ? (
            <div data-testid="typing-indicator" className={styles.typing}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          ) : (
            <div
              data-testid="skeleton-bubble"
              className={`${styles.message} ${styles.assistant}`}
            >
              <div className={`${styles.bubble} ${styles['image-bubble']}`}>
                <div className={styles['skeleton-inner']}>
                  <CircularProgress size={SPINNER_SIZE} />
                  <span className={styles['skeleton-label']}>Generating image</span>
                </div>
              </div>
            </div>
          )
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={styles['input-area']}>
        <TextField
          className={styles.input}
          value={prompt}
          onChange={onPromptChange}
          onKeyDown={onKeyDown}
          multiline
          minRows={2}
          maxRows={6}
          placeholder={isCaption ? 'Describe the caption you need...' : 'Describe the image you need...'}
          variant="standard"
          disabled={loading}
          slotProps={{ input: { disableUnderline: true, sx: { padding: '12px' } } }}
        />
        <div className={styles.options}>
          {!isCaption && (
            <PPSelect
              className={styles.ratio}
              options={ASPECT_RATIO_OPTIONS}
              selectedValue={ratio}
              onSelectionChange={setRatio}
              withCaret
              raised
            />
          )}
          <div className={styles['right-actions']}>
            {mode === 'image' && <UsageIndicator used={imageRequests} total={imageRequestsLimit} />}
            <Tooltip title="Start over">
              <span>
                <IconButton
                  size="small"
                  aria-label="Start over"
                  disabled={messages.length === 0 || loading}
                  onClick={onNewConversation}
                >
                  <Icon path={mdiAutorenew} size={ICON_SIZE} />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              className={styles['btn-generate']}
              variant="contained"
              color="primary"
              disabled={!prompt.trim() || loading}
              onClick={onSend}
              startIcon={<Icon path={mdiAutoFix} size={ICON_SIZE} />}
            >
              Generate
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GenerateTab
